import { Activity, AlertTriangle, Calendar, MapPin, Radio } from 'lucide-react';
import type { StatusDispositivo, TipoDispositivoLeitura } from '../../types/models';
import { STATUS_LABEL, TIPO_LABEL, statusAccent } from '../../pages/admin/dispositivos-shared';

export type DispositivoEditRow = {
  id: string;
  nome: string;
  codigo: string;
  tipo: TipoDispositivoLeitura;
  status: StatusDispositivo;
  ativo: boolean;
  criadoEm?: string;
  atualizadoEm?: string;
  ultimoHeartbeatEm?: string;
  localInstalacao?: string;
  _count?: { eventosAcesso?: number; incidentes?: number };
};

function fmtAudit(iso?: string) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return '—';
  }
}

export function DispositivoEditSidePanel({ dispositivo }: { dispositivo: DispositivoEditRow }) {
  const accent = statusAccent(dispositivo.status, dispositivo.ativo);
  const statusTxt = dispositivo.ativo
    ? (STATUS_LABEL[dispositivo.status] || dispositivo.status)
    : 'Cadastro inativo';
  const eventos = dispositivo._count?.eventosAcesso ?? 0;
  const incidentes = dispositivo._count?.incidentes ?? 0;
  const emUso = eventos > 0;

  return (
    <div className="ac-modal-dual__side ac-dispositivo-edit-side">
      <div className="ac-modal-dual__side-panel">
        <div className="ac-modal-dual__side-head">
          <Radio size={14} strokeWidth={1.5} />
          <strong>Operação</strong>
        </div>
        <div className="ac-modal-dual__side-body">
          <div
            className="ac-dispositivo-side-status"
            style={{ '--disp-accent': accent } as React.CSSProperties}
          >
            <span className="ac-dispositivo-side-status-dot" aria-hidden />
            <div>
              <span className="ac-dispositivo-side-status-val">{statusTxt}</span>
              <span className="ac-dispositivo-side-status-lbl">Situação atual</span>
            </div>
          </div>

          <div className="ac-dispositivo-side-kpis">
            <div className="ac-dispositivo-side-kpi">
              <Activity size={13} />
              <div>
                <span className="ac-dispositivo-side-kpi-val">{eventos}</span>
                <span className="ac-dispositivo-side-kpi-lbl">Eventos de acesso</span>
              </div>
            </div>
            <div className="ac-dispositivo-side-kpi">
              <AlertTriangle size={13} />
              <div>
                <span className="ac-dispositivo-side-kpi-val">{incidentes}</span>
                <span className="ac-dispositivo-side-kpi-lbl">Incidentes</span>
              </div>
            </div>
          </div>

          <div className="ac-dispositivo-side-meta">
            <div className="ac-dispositivo-side-meta-row">
              <Calendar size={12} />
              <div>
                <span className="ac-dispositivo-side-meta-lbl">Último heartbeat</span>
                <span className="ac-dispositivo-side-meta-val">{fmtAudit(dispositivo.ultimoHeartbeatEm)}</span>
              </div>
            </div>
            <div className="ac-dispositivo-side-meta-row">
              <MapPin size={12} />
              <div>
                <span className="ac-dispositivo-side-meta-lbl">Local cadastrado</span>
                <span className="ac-dispositivo-side-meta-val">
                  {dispositivo.localInstalacao?.trim() || '—'}
                </span>
              </div>
            </div>
          </div>

          <p className="ac-dispositivo-side-hint">
            Tipo: <strong>{TIPO_LABEL[dispositivo.tipo] || dispositivo.tipo}</strong>
            {' · '}
            Código <code>{dispositivo.codigo}</code>
          </p>

          {emUso && (
            <p className="ac-dispositivo-side-hint ac-dispositivo-side-hint--info">
              Já registrou acessos — altere o código apenas se o hardware foi trocado.
            </p>
          )}

          <dl className="ac-dispositivo-side-audit">
            <div>
              <dt>Inclusão</dt>
              <dd>{fmtAudit(dispositivo.criadoEm)}</dd>
            </div>
            <div>
              <dt>Alteração</dt>
              <dd>{fmtAudit(dispositivo.atualizadoEm)}</dd>
            </div>
          </dl>
        </div>
      </div>
    </div>
  );
}
